import { type CheckboxOptionType } from 'antd';
import { sortBy } from 'lodash';
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react';

import { Spinner } from 'components/ui';
import { TaskStepsDivider } from 'horizon/routes/WorkOrders/Tasks/TaskTemplates/TaskTemplates.style';
import {
  type AtlasGqlTaskStepFieldType,
  useGetTaskStepsForTaskQuery,
  useGetTaskTemplateQuery,
} from 'types/atlas-graphql';

import { StyledCheckboxGroup } from './ApplyTaskTemplateButton.style';

export type TaskStep = {
  name: string;
  fields: TaskStepFieldJSON[];
  priority: number;
  description?: string;
};

export type TaskStepFieldJSON = {
  type: AtlasGqlTaskStepFieldType;
  label: string;
  options: string[] | null;
  date_value: string | null;
  text_value: string | null;
  float_value: string | null;
  is_required: boolean;
  select_value: string | null;
  display_order: number;
  multi_select_value: string | null;
  time_range_end_value: string | null;
  time_range_start_value: string | null;
  can_duplicate: boolean;
};

type ApplyTaskTemplateStepChooserProps = {
  taskId?: string;
  templateId: string;
  selectedTaskSteps: TaskStep[];
  setSelectedTaskSteps: Dispatch<SetStateAction<TaskStep[]>>;
  /** Called when the template has no selectable steps. Useful in creation flows to skip the chooser UI and proceed immediately. */
  onNoSteps?: () => void;
  showTopDivider?: boolean;
  showBottomDivider?: boolean;
};

export const ApplyTaskTemplateStepChooser = ({
  taskId,
  templateId,
  selectedTaskSteps = [],
  setSelectedTaskSteps,
  onNoSteps,
  showTopDivider,
  showBottomDivider,
}: ApplyTaskTemplateStepChooserProps) => {
  const [hasNoSteps, setHasNoSteps] = useState<boolean>(false);

  const { data: existingTaskStepData, loading: existingTaskStepsLoading } =
    useGetTaskStepsForTaskQuery({
      variables: { taskId: taskId ?? '' },
      skip: !taskId,
    });
  const { data: templateData, loading: templateLoading } = useGetTaskTemplateQuery({
    variables: { taskTemplateId: templateId },
  });

  const { templateTaskSteps, validTaskSteps, taskStepOptions } = useMemo(() => {
    const existingTaskStepNames =
      existingTaskStepData?.taskStepsForTask?.map((step) => step?.name ?? '').filter(Boolean) ?? [];

    const templateTaskSteps: TaskStep[] = sortBy(
      templateData?.taskTemplate?.template['taskSteps'] ?? [],
      'priority'
    );
    const validTaskSteps = templateTaskSteps.filter(
      (ts) => !existingTaskStepNames.includes(ts.name)
    );

    const taskStepOptions: CheckboxOptionType[] = templateTaskSteps.map((ts) => ({
      label: ts.name,
      value: ts.name,
      disabled: existingTaskStepNames.includes(ts.name),
    }));

    return {
      templateTaskSteps,
      validTaskSteps,
      taskStepOptions,
    };
  }, [existingTaskStepData, templateData]);

  const loading = existingTaskStepsLoading || templateLoading;

  // reset selections when templateId changes and data is ready; call onNoSteps if there's nothing to select
  useEffect(() => {
    if (!loading) {
      setSelectedTaskSteps(validTaskSteps);
      setHasNoSteps(validTaskSteps.length === 0);
      if (validTaskSteps.length === 0) {
        onNoSteps?.();
      }
    }
  }, [loading, setSelectedTaskSteps, validTaskSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  // In creation flows (no taskId), hide the chooser entirely when the template has no steps
  if (!loading && !taskId && validTaskSteps.length === 0) {
    return null;
  }

  return loading ? (
    <Spinner />
  ) : (
    <div>
      {showTopDivider && !hasNoSteps && <TaskStepsDivider />}
      <StyledCheckboxGroup
        name="taskSteps"
        value={selectedTaskSteps.map((ts) => ts.name)}
        options={taskStepOptions}
        onChange={(checkedValues: string[]) => {
          setSelectedTaskSteps(templateTaskSteps.filter((ts) => checkedValues.includes(ts.name)));
        }}
      />
      {showBottomDivider && !hasNoSteps && <TaskStepsDivider />}
    </div>
  );
};
